import {
  KEY_ACTION_IDS,
  type KeyAction,
  KeyActionSchema,
  type KeyBinding,
  KeyBindingSchema,
} from '@shefira/shared';
import { z } from 'zod';

import { uiFormat, uiText } from '../ui/text.js';

/**
 * The player's keys, in one place: every shortcut of the game asks here
 * whether a key press is its own, and the settings window rebinds them.
 *
 * A binding is one of two kinds. A letter is matched by the character it
 * types (`event.key`), so the M is where it is written on an AZERTY as on a
 * QWERTY. Anything else — a digit, the space bar, an arrow — is matched by
 * its position (`event.code`): the digit row of an AZERTY types `&`, `é`,
 * `"` without Shift, so a digit's character would miss every press. Modifiers
 * are never part of a binding; Escape closes windows and Enter opens the
 * chat, so neither can be taken. The mouse's side buttons arrive as codes
 * `Mouse4` / `Mouse5` (`settings/mouse-buttons.ts`).
 *
 * Preferences of the interface, remembered in `localStorage` for this
 * browser and saved on the account by `settings/sync.ts` (ADR 0029); the
 * server stores them and never reads them.
 */

const STORAGE_KEY = 'shefira.keys';

export { KEY_ACTION_IDS, type KeyAction, type KeyBinding };

/** What a binding reads of a key press: the event's key, code and modifiers. */
export type KeyPress = Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'altKey'>;

/** Every rebindable action's name in the settings window. */
export const KEY_ACTIONS: Readonly<Record<KeyAction, string>> = {
  inventory: 'Inventaire',
  map: 'Carte du monde',
  travel: 'Portails',
  profile: 'Profil',
  achievements: 'Succès',
  quests: 'Journal de quêtes',
  spellbook: 'Grimoire',
  settings: 'Réglages',
  shop: 'Boutique',
  friends: 'Amis',
  guild: 'Guilde',
  ladder: 'Classement',
  bestiary: 'Carnet de chasse',
  auction: 'Hôtel des ventes (ancien raccourci)',
  mount: 'Monter / descendre',
  potion: 'Boire une potion de vie',
  target: 'Cibler / attaquer',
  targetNext: 'Cible suivante',
  interact: 'Interagir',
  spell1: 'Sort 1',
  spell2: 'Sort 2',
  spell3: 'Sort 3',
  spell4: 'Sort 4',
  walkUp: 'Marcher vers le haut',
  walkDown: 'Marcher vers le bas',
  walkLeft: 'Marcher vers la gauche',
  walkRight: 'Marcher vers la droite',
};

export const DEFAULT_BINDINGS: Readonly<Record<KeyAction, KeyBinding>> = {
  inventory: { key: 'i' },
  map: { key: 'm' },
  travel: { key: 't' },
  profile: { key: 'p' },
  achievements: { key: 'j' },
  quests: { key: 'u' },
  spellbook: { key: 'k' },
  settings: { key: 'o' },
  shop: { key: 'b' },
  friends: { key: 'f' },
  guild: { key: 'g' },
  ladder: { key: 'l' },
  bestiary: { key: 'n' },
  auction: { key: 'h' },
  mount: { key: 'r' },
  potion: { key: 'e' },
  target: { code: 'Space' },
  targetNext: { code: 'Tab' },
  // C sits under the walking keys on an AZERTY as on a QWERTY; E is the potion's.
  interact: { key: 'c' },
  spell1: { code: 'Digit1' },
  spell2: { code: 'Digit2' },
  spell3: { code: 'Digit3' },
  spell4: { code: 'Digit4' },
  // By position: ZQSD on an AZERTY, WASD on a QWERTY, the same four keys.
  walkUp: { code: 'KeyW' },
  walkDown: { code: 'KeyS' },
  walkLeft: { code: 'KeyA' },
  walkRight: { code: 'KeyD' },
};

/**
 * The defaults before walking with the keys took the left hand's letters
 * (W, A, Q sit under ZQSD / WASD): an account that still holds one of them
 * gets the new default, or one press would both walk and mount.
 */
const FORMER_DEFAULTS: Partial<Record<KeyAction, KeyBinding>> = {
  mount: { key: 'w' },
  potion: { key: 'a' },
  quests: { key: 'q' },
};

const StoredSchema = z.partialRecord(KeyActionSchema, KeyBindingSchema);

/** Keys that keep their meaning everywhere and so cannot be bound. */
const RESERVED_CODES = new Set(['Escape', 'Enter', 'NumpadEnter']);

/**
 * The letter each key position types on this keyboard, learnt from the
 * presses (and from the browser where it tells): a position binding is
 * named by its letter, Z for the AZERTY's KeyW, and found taken by it.
 */
const layout = new Map<string, string>();

let layoutNoticePending = false;

/** A position found to type another letter than guessed: labels and tooltips follow, once. */
export function learnLayout(code: string, key: string): void {
  if (!/^Key[A-Z]$/.test(code) || !/^\p{L}$/u.test(key)) return;
  const letter = key.toLowerCase();
  const guessed = letterAt(code);
  layout.set(code, letter);
  if (guessed === letter || layoutNoticePending) return;
  layoutNoticePending = true;
  queueMicrotask(() => {
    layoutNoticePending = false;
    for (const listener of listeners) listener();
  });
}

/** The letter typed at a `KeyX` position: the learnt one, else the QWERTY's. */
function letterAt(code: string): string | undefined {
  const known = layout.get(code);
  if (known !== undefined) return known;
  const qwerty = /^Key([A-Z])$/.exec(code);
  return qwerty === null ? undefined : (qwerty[1] ?? '').toLowerCase();
}

const listeners = new Set<() => void>();
let bindings: Record<KeyAction, KeyBinding> = load();

function load(): Record<KeyAction, KeyBinding> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_BINDINGS };
    const parsed = StoredSchema.safeParse(JSON.parse(raw));
    return parsed.success ? withDefaults(parsed.data) : { ...DEFAULT_BINDINGS };
  } catch {
    return { ...DEFAULT_BINDINGS };
  }
}

/** Saved bindings over the defaults, a former default moved to its new key. */
function withDefaults(
  saved: Partial<Record<KeyAction, KeyBinding>>,
): Record<KeyAction, KeyBinding> {
  const merged = { ...DEFAULT_BINDINGS, ...saved };
  for (const action of KEY_ACTION_IDS) {
    const held = merged[action];
    const former = FORMER_DEFAULTS[action];
    if (former === undefined || !('key' in held) || !('key' in former) || held.key !== former.key) {
      continue;
    }
    // The new default taken by another of the player's keys: the old one
    // stays, and the walk holds that position until the player rebinds it.
    const next = DEFAULT_BINDINGS[action];
    const taken = KEY_ACTION_IDS.some(
      (other) => other !== action && other !== 'auction' && sameBinding(merged[other], next),
    );
    if (!taken) merged[action] = next;
  }
  return merged;
}

if (typeof window !== 'undefined') {
  window.addEventListener(
    'keydown',
    (event) => {
      learnLayout(event.code, event.key);
    },
    { capture: true },
  );
  // Chromium knows the layout before any press; the others learn it as keys are used.
  const keyboard = (
    navigator as Navigator & {
      keyboard?: { getLayoutMap?: () => Promise<ReadonlyMap<string, string>> };
    }
  ).keyboard;
  void keyboard
    ?.getLayoutMap?.()
    .then((map) => {
      map.forEach((key, code) => {
        learnLayout(code, key);
      });
    })
    .catch(() => undefined);
}

function save(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    /* the bindings still hold for this visit */
  }
  for (const listener of listeners) listener();
}

/** The numpad's digits count as the row's: a spell bound to 1 answers to both. */
function normaliseCode(code: string): string {
  const numpad = /^Numpad(\d)$/.exec(code);
  return numpad === null ? code : `Digit${numpad[1] ?? ''}`;
}

export function bindingOf(action: KeyAction): KeyBinding {
  return bindings[action];
}

export function setBinding(action: KeyAction, binding: KeyBinding): void {
  bindings = { ...bindings, [action]: binding };
  save();
}

export function resetBindings(): void {
  bindings = { ...DEFAULT_BINDINGS };
  save();
}

/** All the bindings at once, as the account saved them; the defaults fill the gaps. */
export function replaceBindings(saved: Partial<Record<KeyAction, KeyBinding>>): void {
  bindings = withDefaults(saved);
  save();
}

/** Every binding, for the account save. */
export function allBindings(): Readonly<Record<KeyAction, KeyBinding>> {
  return bindings;
}

export function onBindingsChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The same key: a letter and a position match when the letter sits there on this keyboard. */
export function sameBinding(a: KeyBinding, b: KeyBinding): boolean {
  if ('key' in a) return 'key' in b ? a.key === b.key : letterAt(b.code) === a.key;
  return 'code' in b ? a.code === b.code : letterAt(a.code) === b.key;
}

/** The action already holding this binding, if it is another one. */
export function conflictOf(action: KeyAction, binding: KeyBinding): KeyAction | undefined {
  return KEY_ACTION_IDS.find(
    (other) => other !== action && other !== 'auction' && sameBinding(bindings[other], binding),
  );
}

/**
 * Binds the key, and gives the action that held it this one's former key:
 * the two swap. Returns the action that was moved, if any.
 */
export function swapBinding(action: KeyAction, binding: KeyBinding): KeyAction | undefined {
  const taken = conflictOf(action, binding);
  bindings =
    taken === undefined
      ? { ...bindings, [action]: binding }
      : { ...bindings, [action]: binding, [taken]: bindings[action] };
  save();
  return taken;
}

/**
 * Whether a key press is this action's. Only the key itself: whether the
 * player is typing in the chat, or holding the key down, is the caller's call.
 */
export function isAction(event: KeyPress, action: KeyAction): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  const binding = bindings[action];
  const code = normaliseCode(event.code);
  if ('code' in binding) return code === binding.code;
  // A position bound to another action holds that key whatever it types:
  // one press is one action (an old W for the mount under ZQSD's Z).
  return (
    event.key.toLowerCase() === binding.key &&
    !KEY_ACTION_IDS.some((other) => {
      const held = bindings[other];
      return other !== action && 'code' in held && held.code === code;
    })
  );
}

/**
 * The binding a key press would make, for the settings window: a letter by
 * its character, anything else by its position. Reserved keys and modifiers
 * on their own give nothing.
 */
export function captureBinding(event: KeyPress): KeyBinding | undefined {
  if (event.metaKey || event.ctrlKey || event.altKey) return undefined;
  const code = normaliseCode(event.code);
  if (RESERVED_CODES.has(code) || code === '' || /^(Shift|Control|Alt|Meta)/.test(code)) {
    return undefined;
  }
  if (/^\p{L}$/u.test(event.key)) return { key: event.key.toLowerCase() };
  return { code };
}

const CODE_LABELS: Record<string, string> = {
  Space: 'Espace',
  Tab: 'Tab',
  Backspace: 'Retour',
  Delete: 'Suppr',
  ArrowUp: 'Haut',
  ArrowDown: 'Bas',
  ArrowLeft: 'Gauche',
  ArrowRight: 'Droite',
  Home: 'Début',
  End: 'Fin',
  PageUp: 'Page haut',
  PageDown: 'Page bas',
  Insert: 'Inser',
  CapsLock: 'Verr. maj',
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  NumpadAdd: 'Num +',
  NumpadSubtract: 'Num -',
  NumpadMultiply: 'Num *',
  NumpadDivide: 'Num /',
  NumpadDecimal: 'Num .',
  Mouse4: 'Souris 4',
  Mouse5: 'Souris 5',
};

/** What the key is called on screen: « Espace », « 1 », « M ». */
export function bindingLabel(binding: KeyBinding): string {
  if ('key' in binding) return binding.key.toUpperCase();
  const known = CODE_LABELS[binding.code];
  if (known !== undefined) return known;
  const digit = /^Digit(\d)$/.exec(binding.code);
  if (digit !== null) return digit[1] ?? '';
  const letter = letterAt(binding.code);
  if (letter !== undefined) return letter.toUpperCase();
  return binding.code;
}

/** The key an action answers to, named in the player's language: « R », « Espace ». */
export function actionKeyLabel(action: KeyAction): string {
  return uiText(bindingLabel(bindings[action]), 'key');
}

/**
 * « Inventaire (I) » — a button's tooltip, with the key it answers to, in the
 * player's language (a `uiFormat` result, which `uiText` leaves alone).
 */
export function shortcutTitle(label: string, action: KeyAction): string {
  return uiFormat('{label} ({key})', { label: uiText(label), key: actionKeyLabel(action) });
}
